import React, { useEffect, useContext, useCallback } from 'react';
import { PAGED_ENGINE_SCORING_GRID_QUERY } from './queries';
import { ENGINE_SCORING_GRID_DELETED } from './subscriptions';
import { ServiceContext } from 'providers/ServiceProvider';
import { useTranslation } from 'react-i18next';
import { Card } from '@totalsoft/rocket-ui';
import { useApolloClient } from '@apollo/client';
import { getListBrowserStore } from '@ts-private/list-browser';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { ExportModalComponent } from 'components/common/export/ExportModalComponent';
import { useToast } from 'hooks/toasts/useToast';
import { useMemo } from 'react';
import { useMutation } from 'hooks/apollo/useMutation';
import { DELETE_ENGINE_SCORING_GRID } from './mutations';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import EngineScoringItem from './EngineScoringItem';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import useSubscription from 'hooks/apollo/useSubscription';

export const engineScoringGridsStore = getListBrowserStore({
  filterDefinitions: [
    {
      name: 'name',
      caption: 'EngineScoringGrid.Name',
      type: 'text'
    },
    {
      name: 'code',
      caption: 'EngineScoringGrid.Code',
      type: 'text'
    },
    {
      name: 'isHistory',
      caption: 'EngineScoringGrid.IsHistory',
      type: 'boolean'
    },
    {
      name: 'isActive',
      caption: 'EngineScoringGrid.Active',
      type: 'boolean'
    }
  ],
  orderByFields: [
    {
      caption: 'EngineScoringGrid.Name',
      value: 'Name'
    },
    {
      caption: 'EngineScoringGrid.Code',
      value: 'Code'
    }
  ],
  pager: {
    orderBy: 'Name',
    direction: 'asc'
  }
});

const EngineScoringListBrowser = () => {
  const [t] = useTranslation();
  const addToast = useToast();
  const client = useApolloClient();
  const confirm = useConfirmDialog();
  const [activeService] = useContext(ServiceContext);

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: PAGED_ENGINE_SCORING_GRID_QUERY,
        name: 'pagedEngineScoringGrids'
      }
    }),
    []
  );

  // #region Delete

  useSubscription(ENGINE_SCORING_GRID_DELETED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('EngineScoringGrid.EngineScoringGridRemoved'), 'success');
      client.cache.evict({ id: `EngineScoringGridItem:{"engineScoringId":${data.engineScoringGridDeleted.engineScoringId}}` });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedEngineScoringGrids' });
      client.cache.gc();
    }
  });

  const [deleteEngineScoringGrid] = useMutation(DELETE_ENGINE_SCORING_GRID);

  const handleDelete = useCallback(
    engineScoringId => {
      confirm(
        () => {
          deleteEngineScoringGrid({ variables: { input: { engineScoringId } } });
        },
        {
          content: t('EngineScoringGrid.DeleteEngineScoring')
        }
      );
    },
    [deleteEngineScoringGrid, confirm, t]
  );

  // #endregion

  useEffect(() => {
    if (activeService) {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedEngineScoringGrids' });
      client.cache.gc();
    }
  }, [activeService, client]);

  const itemComponent = useCallback(({ data }) => <EngineScoringItem data={data} onDelete={handleDelete} />, [handleDelete]);

  const columns = useMemo(
    () => [
      { name: 'name', caption: 'EngineScoringGrid.Name' },
      { name: 'code', caption: 'EngineScoringGrid.Code' },
      { name: 'version', caption: 'EngineScoringGrid.Version', width: '100px', align: 'center' },
      { name: 'active', caption: 'EngineScoringGrid.Active', width: '100px', align: 'center' },
      { name: 'validFrom', caption: 'EngineScoringGrid.ValidFrom' },
      { name: 'validTo', caption: 'EngineScoringGrid.ValidTo' },
      { name: 'actions', caption: 'Actions', width: '120px' }
    ],
    []
  );

  return (
    <Card icon={ListAltIcon}>
      <TableListBrowser name='engine-scoring-browser' store={engineScoringGridsStore} ItemComponent={itemComponent} columns={columns} />
      <ExportModalComponent store={engineScoringGridsStore} options={fetchingOptions} />
      <ApolloFetchingComponent store={engineScoringGridsStore} options={fetchingOptions} />
    </Card>
  );
};

export default EngineScoringListBrowser;
